import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDealBySlug, type ApiError, type DealDetail } from '@services/api';
import { useAuth } from '@contexts/AuthContext';
import { storeReturnAfterLogin } from '@utils/returnAfterLogin';
import { LoadingBlock } from '@components/LoadingBlock';
import { DealNotFoundPage } from '@pages/broker/DealNotFoundPage';

export interface DealBySlugGateProps {
  lang: string;
  slug: string;
  variant: 'main' | 'sub';
  children: (detail: DealDetail) => React.ReactNode;
}

/**
 * Loads deal by Latin url slug. No global toasts — 403 → access-denied route, 404 → not-found page,
 * other errors → not-found (avoid leaking server details).
 */
export function DealBySlugGate({ lang, slug, variant, children }: DealBySlugGateProps) {
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const [detail, setDetail] = useState<DealDetail | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset state before async fetch
    setDetail(null);
    setNotFound(false);
    const s = String(slug ?? '').trim();
    if (!s) {
      setNotFound(true);
      return () => {
        cancelled = true;
      };
    }
    void getDealBySlug(s)
      .then(({ data }) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        if (err.status === 401) {
          clearSession();
          if (typeof window !== 'undefined') {
            storeReturnAfterLogin(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            );
          }
          navigate(`/${lang}/signup`, { replace: true });
          return;
        }
        if (err.status === 403) {
          navigate(`/${lang}/access-denied`, { replace: true });
          return;
        }
        if (err.status === 404) {
          setNotFound(true);
          return;
        }
        setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, lang, variant, navigate, clearSession]);

  if (notFound) {
    return <DealNotFoundPage lang={lang} />;
  }
  if (!detail?.id) {
    return <LoadingBlock className="min-h-[40vh]" />;
  }
  return <>{children(detail)}</>;
}
